import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于第一版主',
  description: '第一版主致力于为论坛运营者与版主候选人提供最全面、最实用的版块管理知识与技能指导。我们的团队由资深论坛管理员、社区运营专家和技术研发人员组成。',
  url: 'https://diyibanzhu.com/about',
  about: {
    '@type': 'Organization',
    name: '第一版主',
    description: '第一版主是专业的论坛版块管理知识平台，提供版主职责、权限、任命流程与管理技巧的深度解析。',
    url: 'https://diyibanzhu.com/',
  },
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们 - 第一版主 | 论坛版块管理核心指南"
        description="第一版主致力于为论坛运营者与版主候选人提供最全面、最实用的版块管理知识与技能指导。了解我们的品牌故事、专业口碑与联系方式。"
        keywords="第一版主,关于我们,论坛管理,版主培训,联系我们"
        canonicalUrl="https://diyibanzhu.com/about"
        ogTitle="关于我们 - 第一版主"
        ogDescription="了解第一版主的品牌故事、专业口碑与联系方式。致力于为论坛运营者提供最全面的版块管理知识。"
        ogUrl="https://diyibanzhu.com/about"
        jsonLdScripts={[aboutJsonLd]}
      />

      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <section className="w-full py-10 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
              关于第一版主
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
              专注论坛版块管理知识分享，助力每一位版主成长
            </p>
          </div>

          <div id="brand" className="mb-14 md:mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong>第一版主</strong>诞生于一群资深论坛管理员对社区管理经验的深度思考。在多年的论坛运营实践中，我们发现：一个版块的好坏，很大程度上取决于版主的能力水平，尤其是第一版主的综合管理素养。
                  </p>
                  <p>
                    然而，行业内长期缺乏系统化的版主管理知识体系。许多新任版主只能靠"摸着石头过河"，在试错中积累经验。这不仅影响了个人的成长效率，也制约了社区的健康发展。
                  </p>
                  <p>
                    因此，我们创建了第一版主这个平台，旨在为论坛运营者和版主候选人提供<strong>系统化、结构化、实用化的版块管理知识</strong>。从职责权限到任命流程，从管理技巧到晋升指南，我们希望每一位版主都能在这里找到成长的方向和动力。
                  </p>
                </div>
              </div>
              <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20collaboration%20with%20warm%20lighting%2C%20professional%20Asian%20team%20discussing%20around%20table%2C%20minimalist%20workspace%20design%2C%20clean%20cream%20and%20beige%20tones%2C%20editorial%20style%20photography%2C%20natural%20light&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="第一版主团队"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>

          <div id="reputation" className="mb-14 md:mb-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              专业口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              <div className="p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-md bg-primary-100/70 text-primary-600">
                  <i className="ri-article-line text-xl"></i>
                </div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-1">40+</div>
                <p className="text-sm text-foreground-500">深度原创文章</p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-md bg-accent-100/70 text-accent-600">
                  <i className="ri-group-line text-xl"></i>
                </div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600 mb-1">50,000+</div>
                <p className="text-sm text-foreground-500">累计服务版主</p>
              </div>
              <div className="p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-md bg-secondary-100/70 text-secondary-600">
                  <i className="ri-star-line text-xl"></i>
                </div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-secondary-600 mb-1">98%</div>
                <p className="text-sm text-foreground-500">用户满意度</p>
              </div>
            </div>
          </div>

          <div id="contact" className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-10">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                有任何问题或建议，欢迎通过以下方式与我们取得联系
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100/70 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-500">contact@diyibanzhu.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md bg-accent-100/70 text-accent-600">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">办公地址</h3>
                    <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md bg-secondary-100/70 text-secondary-600">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactForm() {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      form.reset();
      return;
    }
    const formData = new FormData(form);
    formData.delete('website_alt');
    const url = 'https://readdy.ai/api/form/d9qatpcnlsngrm4llusg';
    const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
      .then(async (response) => {
        const responseText = await response.text();
        return { response, responseText };
      })
      .then(({ response, responseText }) => {
        try {
          const data = JSON.parse(responseText);
          const serverMsg = data?.meta?.message || data?.message || '';
          if (response.ok && data?.code === 'OK') {
            form.reset();
            alert('感谢您的留言！我们会尽快回复。');
          } else {
            alert(serverMsg || '提交失败，请稍后再试。');
          }
        } catch {
          alert('提交失败，请稍后再试。');
        }
      })
      .catch(() => {
        alert('网络错误，请稍后再试。');
      });
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          姓名
        </label>
        <input
          type="text"
          id="contact-name"
          name="name"
          required
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800 placeholder:text-foreground-300"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          邮箱
        </label>
        <input
          type="email"
          id="contact-email"
          name="email"
          required
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800 placeholder:text-foreground-300"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          type="text"
          id="contact-subject"
          name="subject"
          required
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800 placeholder:text-foreground-300"
          placeholder="请输入主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-800 placeholder:text-foreground-300 resize-none"
          placeholder="请输入您的留言内容（500字以内）"
        ></textarea>
      </div>
      <div className="contact-honeypot-wrap">
        <label htmlFor="contact-website-alt">Website</label>
        <input
          type="text"
          id="contact-website-alt"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md"
        />
      </div>
      <button
        type="submit"
        className="w-full px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer"
      >
        提交留言
      </button>
    </form>
  );
}